import {
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Stack,
  Typography,
  Box,
  Textarea,
} from "@mui/joy";
import { useDispatch, useSelector } from "react-redux";
import { setProfile, setShowCreateProfileModal } from "../../store";
import API_SERVICE from "../../utils/apiService";
import { useState } from "react";
import { useDebounce } from "../../utils/customHooks";
import AuthLogo from "../auth/AuthLogo";
import { COLOR_BLACK, COLOR_PRIMARY } from "../../utils/styles";
import mascotMemberSignup from "../../assets/mascot_member-signup.png";
import TextInput from "../common/TextInput";

function CreateProfileModal() {
  const dispatch = useDispatch();
  const modalOpen = useSelector((state) => state.modal.showCreateProfileModal);

  // form attributes
  const [username, setUsername] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [city, setCity] = useState(null);
  const [bio, setBio] = useState(null);
  const [error, setError] = useState(null);

  // loading username
  const [isAvailable, setIsAvailable] = useState(null);
  const [loading, setLoading] = useState(false);

  // thank you page state
  const [showThankYou, setShowThankYou] = useState(false);

  // Call API when username changes
  const checkUsernameAvailability = async (name) => {
    if (!name) {
      return;
    }
    setLoading(true);
    try {
      const available = await API_SERVICE.usernameExists(name);
      setIsAvailable(available);
    } catch (error) {
      console.error("Error checking username:", error);
      setIsAvailable(null);
    }
    setLoading(false);
  };
  useDebounce(() => {
    checkUsernameAvailability(username);
  }, [username]);

  async function resetData() {
    setUsername(null);
    setFirstName(null);
    setLastName(null);
    setCity(null);
    setBio(null);
    setError(null);
  }

  return (
    <Modal
      open={modalOpen}
      onClose={() => {
        setShowThankYou(false);
        dispatch(setShowCreateProfileModal(false));
      }}
    >
      <ModalDialog sx={{
        overflowY: "auto",
        padding: "80px",
      }}>
        {showThankYou ? (
          <Stack alignItems="center" spacing={4} sx={{ minWidth: 320, minHeight: 200, justifyContent: 'center' }}>
            <AuthLogo size="100px" />
            <Typography level="h3" fontWeight="bold" sx={{ fontSize: 32 }}>You joined CorkAtlas</Typography>
            <Typography level="body-sm" sx={{ textAlign: 'center', fontSize: 14, color: '#000', lineHeight: 1.2 }}>
              You can now contribute to the wine community<br />
              by adding restaurants or posting in the forums.<br />
              Thank you for your support!
            </Typography>
            <Box component="img" src={mascotMemberSignup} alt="Mascot cheers" sx={{ width: 220, height: 'auto', my: 1 }} />
            <Button
              onClick={() => {
                setShowThankYou(false);
                dispatch(setShowCreateProfileModal(false));
              }}
              sx={{ mt: 2, borderRadius: '24px', backgroundColor: '#202030', color: '#fff', '&:hover': { backgroundColor: '#F0B27A' } }}
            >
              {"Let's Go!"}
            </Button>
          </Stack>
        ) : (
          <>
            <AuthLogo size="100px" />
            <Typography level="h3" fontWeight={700}>Create Your Profile</Typography>
            {error && (
              <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                try {
                  const response = await API_SERVICE.createProfile({
                    username: username,
                    firstName: firstName,
                    lastName: lastName,
                    city: city,
                    bio: bio,
                  });
                  if (response.error) {
                    setError(response.error);
                    return;
                  }
                  const profile = await API_SERVICE.getProfile();
                  dispatch(setProfile(profile));
                  resetData();
                  setShowThankYou(true);
                } catch (err) {
                  setError("An error occurred while creating your profile");
                }
              }}
            >
              <Stack spacing={2} sx={{ maxWidth: 400, margin: "auto" }}>
                <FormControl>
                  <FormLabel>Username</FormLabel>
                  <TextInput
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    endDecorator={
                      username !== "" && (
                        loading ? (
                          <CircularProgress size="sm" />
                        ) : isAvailable !== null ? (
                          isAvailable ? (
                            "✅"
                          ) : (
                            "❌"
                          )
                        ) : null
                      )
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  <TextInput
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    placeholder="First Name"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <TextInput
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    placeholder="Last Name"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>City</FormLabel>
                  <TextInput
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                    placeholder="City"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Bio</FormLabel>
                  <Textarea
                    value={bio}
                    onChange={(event) => setBio(event.target.value)}
                    placeholder="Tell us about yourself..."
                    minRows={4}
                    maxRows={4}
                    sx={{
                      '& textarea': {
                        minHeight: '30px',
                      },
                    }}
                    maxLength={500}
                  />
                  <Typography level="body-xs" sx={{ mt: 1, textAlign: 'right', color: bio?.length > 500 ? 'danger.500' : 'text.tertiary' }}>
                    {bio?.length || 0}/500 characters
                  </Typography>
                </FormControl>
                <Button type="submit" sx={{
                  borderRadius: "20px",
                  backgroundColor: COLOR_BLACK,
                  "&:hover": {
                    // override default onhover color
                    backgroundColor: COLOR_PRIMARY,
                  },
                }}>Create Profile</Button>
              </Stack>
            </form>
          </>
        )}
      </ModalDialog>
    </Modal>
  );
}

export default CreateProfileModal;
